import wechatPayLockup from './assets/wechat-pay-lockup.png';

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + width, y, x + width, y + height, radius);
  ctx.arcTo(x + width, y + height, x, y + height, radius);
  ctx.arcTo(x, y + height, x, y, radius);
  ctx.arcTo(x, y, x + width, y, radius);
  ctx.closePath();
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export type PosterKind = 'institution' | 'personal';

export interface BuildPosterOptions {
  kind?: PosterKind;
  amount: number | null;
  qrImageSrc?: string;
  campusName?: string;
}

export async function buildPosterCanvas({
  amount,
  kind = 'institution',
  qrImageSrc,
  campusName = '快乐学教育 · 岳麓校区',
}: BuildPosterOptions): Promise<HTMLCanvasElement> {
  const poster = document.createElement('canvas');
  // 导出宽度严格设定为 1080px（标准高清海报宽度），基于 828 × 1124 进行等比矢量缩放
  const targetWidth = 1080;
  const baseWidth = 828;
  const baseHeight = 1124;
  const scale = targetWidth / baseWidth;
  const targetHeight = Math.round(baseHeight * scale); // 1466px

  poster.width = targetWidth;
  poster.height = targetHeight;
  const ctx = poster.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas 2d context');

  // 缩放到 1080px 宽度绘图空间，确保所有矢量图形、文字及元素布局清晰对齐且高清
  ctx.scale(scale, scale);

  const withAmount = amount !== null && amount > 0;
  const officialLogo = await loadImage(wechatPayLockup);

  // 海报画稿配色：机构蓝色，个人橙色。
  ctx.fillStyle = kind === 'institution' ? '#2f73dc' : '#f28c28';
  ctx.fillRect(0, 0, baseWidth, baseHeight);

  // 2. 底部白色波浪
  ctx.fillStyle = '#fff';
  ctx.beginPath();
  ctx.moveTo(0, 930);
  ctx.quadraticCurveTo(414, 875, 828, 930);
  ctx.lineTo(828, 1124);
  ctx.lineTo(0, 1124);
  ctx.closePath();
  ctx.fill();

  // 3. 顶部推荐标题
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#fff';
  ctx.font = '500 53px -apple-system, "PingFang SC", "Microsoft YaHei", sans-serif';
  ctx.fillText('推荐使用微信支付', 414, 106);

  // 4. 白色中间卡片（扩大高度与上下内边距，给予充足呼吸感）
  ctx.fillStyle = '#fff';
  roundedRect(ctx, 160, 196, 508, 624, 24);
  ctx.fill();

  // 5. 金额（如果有固定金额）
  if (withAmount) {
    ctx.fillStyle = '#121212';
    ctx.font = '700 52px "DIN Alternate", "Arial Narrow", "Helvetica Neue", Arial, sans-serif';
    ctx.fillText(`¥${amount.toFixed(2)}`, 414, 280);
  }

  // 6. 二维码图片
  const qrSize = 354;
  const qrX = 237;
  const qrTop = withAmount ? 342 : 298;

  if (qrImageSrc) {
    try {
      const qrImg = await loadImage(qrImageSrc);
      ctx.drawImage(qrImg, qrX, qrTop, qrSize, qrSize);
    } catch {
      // 容错绘制占位方块
      ctx.fillStyle = '#eee';
      ctx.fillRect(qrX, qrTop, qrSize, qrSize);
    }
  }

  // 7. 校区名称
  ctx.fillStyle = '#202020';
  ctx.font = '400 22px -apple-system, "PingFang SC", "Microsoft YaHei", sans-serif';
  ctx.fillText(campusName, 414, withAmount ? 750 : 704);

  // 8. 微信支付官方 Logo
  ctx.drawImage(officialLogo, 219, 965, 390, 105);

  return poster;
}
